import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlow, MiniMap, Controls, Background, useNodesState, useEdgesState, Panel, NodeMouseHandler, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LdgGraphNode } from './LdgGraphNode';
import { NodeDetailPanel } from './NodeDetailPanel';
import { useLdgGraphData, LdgNodeData } from './useLdgGraphData';
import { MODULES } from '../../../../content';
import { Filter, GitBranch, ChevronDown, ChevronUp, X } from 'lucide-react';
const nodeTypes = {
  ldgNode: LdgGraphNode
};
function LdgGraphExplorerInner() {
  const [activeModules, setActiveModules] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const {
    nodes: initialNodes,
    edges: initialEdges,
    stats
  } = useLdgGraphData(activeModules);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);
  const toggleModule = (moduleId: string) => {
    setActiveModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
    setSelectedNodeId(null);
  };
  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    setSelectedNodeId(node.id);
  }, []);
  const handleSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
  }, []);
  const selectedNodeData = useMemo(() => {
    if (!selectedNodeId) return null;
    const node = nodes.find(n => n.id === selectedNodeId);
    return node ? node.data as LdgNodeData : null;
  }, [selectedNodeId, nodes]);
  const activeFilterCount = activeModules.size;
  return <div className="w-full h-full relative bg-slate-50 flex flex-col" data-id="element-5948" data-mp="184sr2sun6ud">
      {/* ── Mobile: Compact top bar ── */}
      <div className="md:hidden flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-20" data-id="element-5949" data-mp="3sepmwve7auvm">
        {/* Stats row */}
        <div className="flex items-center justify-between px-3 py-2" data-id="element-5950" data-mp="a7jj8290nhev">
          <div className="flex items-center gap-3" data-id="element-5951" data-mp="598a5tqjpaxp">
            <div className="flex items-center gap-1.5" data-id="element-5952" data-mp="57ncvwcujdw5">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" data-id="element-5953" data-mp="3doru225bth03" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5954" data-mp="39o7zn6c50rhn">
                {stats.nodeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5955" data-mp="3nfmi9c24uqo6">nodes</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-5956" data-mp="3io4jmunl126d">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" data-id="element-5957" data-mp="3imjnx1pj7d6x" />
              <span className="text-xs font-bold text-slate-700" data-id="element-5958" data-mp="2uxqzrrf5i9mv">
                {stats.edgeCount}
              </span>
              <span className="text-[10px] text-slate-400" data-id="element-5959" data-mp="2pzp6kdo0gtxo">edges</span>
            </div>
            <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${stats.isValidDAG ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-5960" data-mp="15gdcqhpewjes">
              
              {stats.isValidDAG ? 'DAG ✓' : 'DAG ✗'}
            </div>
          </div>
          <button onClick={() => setPanelOpen(!panelOpen)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${panelOpen ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-5961" data-mp="11ftbp31pwvnd">
            
            <Filter className="w-3.5 h-3.5" data-id="element-5962" data-mp="1t6ppc5vlr4bp" />
            Filter
            {activeFilterCount > 0 && <span className="bg-indigo-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-5963" data-mp="1f7hpdarm7s6g">
                {activeFilterCount}
              </span>}
            {panelOpen ? <ChevronUp className="w-3 h-3" data-id="element-5964" data-mp="3r9kh2opo4rm" /> : <ChevronDown className="w-3 h-3" data-id="element-5965" data-mp="8re24w1cv5jf" />}
          </button>
        </div>

        {/* Collapsible filter chips */}
        {panelOpen && <div className="px-3 pb-3 border-t border-slate-100 pt-2" data-id="element-5966" data-mp="o2cb582t468o">
            <div className="flex flex-wrap gap-1.5" data-id="element-5967" data-mp="jau9ehsym1mn">
              {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-1 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`} data-id="element-5968" data-mp="f6mo6ulpn3t6">
                  
                    {mod.id}
                  </button>;
          })}
            </div>
            {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5969" data-mp="y1becb4sz9oa">
            
                Toon alles
              </button>}
          </div>}
      </div>

      {/* ── Desktop: Floating panels (hidden on mobile) ── */}
      <div className="hidden md:flex absolute top-4 left-4 z-10 flex-col gap-3 pointer-events-none" data-id="element-5970" data-mp="33mxzpk8adw3u">
        {/* Stats Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5971" data-mp="38mu2jg58zhht">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-5972" data-mp="3djkim3h1yd38">
            <div className="p-1 bg-indigo-100 text-indigo-600 rounded-lg" data-id="element-5973" data-mp="3ibu6zf2ir53e">
              <GitBranch className="w-4 h-4" data-id="element-5974" data-mp="3mdfml86ftxha" />
            </div>
            <div data-id="element-5975" data-mp="3mf9yjwzqef98">
              <h2 className="font-bold text-sm text-slate-900 leading-tight" data-id="element-5976" data-mp="3rca5090htk0v">
                Learning Dependency Graph
              </h2>
              <p className="text-[10px] text-slate-500" data-id="element-5977" data-mp="70mf2etw81a">
                Canonical Taxonomy Structure
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5" data-id="element-5978" data-mp="52pqnj1smd3x">
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5979" data-mp="21ydmne5uprr0">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5980" data-mp="26xghy6w14jls">
                Nodes
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5981" data-mp="1yw967dzzu1ob">
                {stats.nodeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5982" data-mp="28tx2w9h5tj94">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5983" data-mp="23utxgnx9pb0m">
                Edges
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5984" data-mp="2hu3d9mhka342">
                {stats.edgeCount}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5985" data-mp="2cuqouxb1knzd">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5986" data-mp="2rj3g5gz6pinj">
                Depth
              </div>
              <div className="font-bold text-sm text-slate-700" data-id="element-5987" data-mp="2mm3hvs8ktk6z">
                {stats.maxDepth}
              </div>
            </div>
            <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100 text-center" data-id="element-5988" data-mp="2wjreyotnn3sb">
              <div className="text-[9px] font-bold text-slate-400 uppercase" data-id="element-5989" data-mp="3f7s3ov2vc06d">
                DAG
              </div>
              <div className={`font-bold text-sm ${stats.isValidDAG ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-5990" data-mp="3k7egj3esbeen">
                
                {stats.isValidDAG ? '✓' : '✗'}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm border border-slate-200 pointer-events-auto w-72" data-id="element-5991" data-mp="3b815i6b0ihrt">
          <div className="flex items-center gap-2 mb-2" data-id="element-5992" data-mp="318ack923oatu">
            <Filter className="w-3.5 h-3.5 text-slate-400" data-id="element-5993" data-mp="31a3hnjx2emzq" />
            <h3 className="font-bold text-xs text-slate-700" data-id="element-5994" data-mp="2nhx4m6p7kw4i">
              Filter op Module
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-5995" data-mp="2rih0z81yjvpl">
            {MODULES.map(mod => {
            const isActive = activeModules.size === 0 || activeModules.has(mod.id);
            return <button key={mod.id} onClick={() => toggleModule(mod.id)} className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors border ${isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-5996" data-mp="2ig7tkr1zkigf">
                  
                  {mod.id}
                </button>;
          })}
          </div>
          {activeModules.size > 0 && <button onClick={() => setActiveModules(new Set())} className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors w-full text-center py-1 bg-indigo-50 rounded-lg" data-id="element-5997" data-mp="2k1vl57jyi4vq">
            
              Toon alles
            </button>}
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div className="flex-1 w-full h-full min-h-0" data-id="element-5998" data-mp="2199x39fajilp">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView fitViewOptions={{
        padding: 0.2
      }} minZoom={0.05} maxZoom={1.5} defaultEdgeOptions={{
        zIndex: 0
      }} data-id="element-5999" data-mp="21auoiq6b6lfc">
          
          <Background color="#cbd5e1" gap={20} size={1} data-id="element-6000" data-mp="6sjij1nqjx4p" />
          <Controls className="bg-white border-slate-200 shadow-sm rounded-lg overflow-hidden" showInteractive={false} data-id="element-6001" data-mp="brmoqpfgc9e9" />
          
          <MiniMap className="hidden md:block bg-white border-slate-200 shadow-sm rounded-xl overflow-hidden" nodeColor={node => {
          const data = node.data as LdgNodeData;
          return data.colorSet?.ring || '#cbd5e1';
        }} maskColor="rgba(248, 250, 252, 0.7)" data-id="element-6002" data-mp="gsk5qyoq6xe6" />
          
        </ReactFlow>
      </div>

      {/* Detail Panel */}
      <NodeDetailPanel nodeData={selectedNodeData} onClose={() => setSelectedNodeId(null)} onSelectNode={handleSelectNode} data-id="element-6003" data-mp="3t8npiifgsl6e" />
      
    </div>;
}
export function LdgGraphExplorer() {
  return <ReactFlowProvider data-id="element-6004" data-mp="1vt6qqiyrnp3">
      <LdgGraphExplorerInner data-id="element-6005" data-mp="31hztfksne5zl" />
    </ReactFlowProvider>;
}